import { AlertTriangle, Trash01 } from '@untitledui/icons';
import { memo, useCallback, useMemo, useSyncExternalStore } from 'react';

import { FileAttachment } from '@/components/files/FileAttachment';
import { Button } from '@/lib/base/buttons/button';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { useCrmObjectSnapshots } from '@/lib/crm/hooks';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { type UploadedFile } from '@/lib/files/service';
import { getPropertyValue } from '@/types/crm';

import { getAttachmentFileMeta } from './fileIdUtils';

interface RecordAttachmentRowProps {
  entityId: string;
  orgId: number;
  onDownload: (uploadedFileId: number, filename: string) => void;
  onRemove?: (fileEntityId: string) => void;
}

export const RecordAttachmentRow = memo(function RecordAttachmentRow({
  entityId,
  orgId,
  onDownload,
  onRemove,
}: RecordAttachmentRowProps) {
  const snapshots = useCrmObjectSnapshots([entityId], orgId);
  const obj = snapshots.get(entityId);

  // Subscribe to fetch-failure changes so the row re-renders when the file's
  // hydration fails, transitioning from `loading` → `failed` instead of
  // spinning on a skeleton forever. Without this, a deleted file entity (or a
  // 4xx/5xx fetch) leaves the row stuck on the skeleton below. Mirrors
  // NoteLinkedRecordsSection's LinkedEntityCard.
  useSyncExternalStore(subscribeToFetchFailures, getFailureVersion);
  const fetchFailed = !obj && hasFailedFetch(entityId);

  const { filename, uploadedFileId } = getAttachmentFileMeta(obj);
  const rawSize = getPropertyValue<string | number>(
    obj?.properties?.ahoy_file_size
  );
  const mimeType =
    getPropertyValue<string>(obj?.properties?.ahoy_mime_type) ?? '';

  const file: UploadedFile = useMemo(
    () => ({
      id: uploadedFileId ?? 0,
      original_filename: filename,
      file_size: Number(rawSize) || 0,
      mime_type: mimeType,
      created_at: '',
    }),
    [uploadedFileId, filename, rawSize, mimeType]
  );

  const handleDownload = useCallback(() => {
    if (uploadedFileId != null) {
      onDownload(uploadedFileId, filename);
    }
  }, [onDownload, uploadedFileId, filename]);

  const handleDelete = useCallback(() => {
    onRemove?.(entityId);
  }, [onRemove, entityId]);

  // The file entity is gone or its fetch failed. Render a recoverable error
  // row (never an eternal skeleton) that still lets the user detach the
  // now-broken association from the note.
  if (fetchFailed) {
    return (
      <div className='flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200'>
        <AlertTriangle className='size-5 shrink-0 text-utility-error-500' />
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-medium text-fg-tertiary truncate'>
            Attachment unavailable
          </div>
          <div className='text-xs text-fg-quaternary'>
            This file could not be loaded.
          </div>
        </div>
        {onRemove && (
          <Button
            type='button'
            size='sm'
            color='tertiary'
            onClick={handleDelete}
            title='Remove attachment'
            aria-label='Remove attachment'
            className='text-error hover:text-error'
          >
            <Trash01 className='size-4' />
          </Button>
        )}
      </div>
    );
  }

  if (!obj) {
    return <Skeleton variant='text' width={160} height={40} />;
  }

  return (
    <FileAttachment
      file={file}
      onDownload={uploadedFileId != null ? handleDownload : undefined}
      onDelete={onRemove ? handleDelete : undefined}
    />
  );
});
